/**
 * Extracts all `data-*` attributes from an object.
 *
 * @param obj - The object to extract data attributes from
 * @returns A new object containing only the data attributes
 *
 * @example
 * ```ts
 * const props = { 'data-testid': 'button', 'aria-label': 'Close', className: 'btn' };
 * const dataAttrs = extractDataAttributes(props);
 * // Result: { 'data-testid': 'button' }
 * ```
 */
export function extractDataAttributes<T extends Record<string, any>>(obj: T): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (key.startsWith('data-')) {
      result[key] = value;
    }
  }

  return result;
}

/**
 * Extracts all `aria-*` attributes from an object.
 *
 * @param obj - The object to extract aria attributes from
 * @returns A new object containing only the aria attributes
 *
 * @example
 * ```ts
 * const props = { 'data-testid': 'button', 'aria-label': 'Close', className: 'btn' };
 * const ariaAttrs = extractAriaAttributes(props);
 * // Result: { 'aria-label': 'Close' }
 * ```
 */
export function extractAriaAttributes<T extends Record<string, any>>(obj: T): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (key.startsWith('aria-')) {
      result[key] = value;
    }
  }

  return result;
}

/**
 * Extracts both `data-*` and `aria-*` attributes from an object in a single pass.
 * This is more performant than calling the individual functions separately.
 *
 * @param obj - The object to extract data and aria attributes from
 * @returns A new object containing only the data and aria attributes
 *
 * @example
 * ```ts
 * const props = { 'data-testid': 'button', 'aria-label': 'Close', className: 'btn', onClick: fn };
 * const attrs = extractDataAriaAttributes(props);
 * // Result: { 'data-testid': 'button', 'aria-label': 'Close' }
 * ```
 */
export function extractDataAriaAttributes<T extends Record<string, any>>(obj: T): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (key.startsWith('data-') || key.startsWith('aria-')) {
      result[key] = value;
    }
  }

  return result;
}

/**
 * Filters out `data-*` and `aria-*` attributes from an object, returning the remaining props.
 * This is useful when you want to separate accessibility attributes from other component props.
 *
 * @param obj - The object to filter
 * @returns A tuple containing [remainingProps, dataAriaAttributes]
 *
 * @example
 * ```ts
 * const props = { 'data-testid': 'button', 'aria-label': 'Close', className: 'btn', onClick: fn };
 * const [otherProps, attrs] = separateDataAriaAttributes(props);
 * // otherProps: { className: 'btn', onClick: fn }
 * // attrs: { 'data-testid': 'button', 'aria-label': 'Close' }
 * ```
 */
export function separateDataAriaAttributes<T extends Record<string, any>>(
  obj: T,
): [Record<string, unknown>, Record<string, unknown>] {
  const remainingProps: Record<string, unknown> = {};
  const dataAriaAttributes: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (key.startsWith('data-') || key.startsWith('aria-')) {
      dataAriaAttributes[key] = value;
    } else {
      remainingProps[key] = value;
    }
  }

  return [remainingProps, dataAriaAttributes];
}
